import {useMutation} from '@apollo/client';
import {
  DndContext,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {
  ReorderAccountsDocument,
  ReorderAccountTypesDocument,
  ReorderStarredAccountsDocument,
} from '@transactions/graphql';
import {GripVertical, Star} from 'lucide-react';
import * as React from 'react';
import {toSectionAccounts} from 'src/AccountsView/accountBalances';
import {orderSections} from 'src/AccountsView/accountFilter';
import {AccountRowV2} from 'src/AccountsView/AccountRowV2';
import {AccountSection} from 'src/AccountsView/AccountSection';
import {setAccountTypeOrder} from 'src/apolloCache/accountTypeOrder';
import {setStarredAccountIds} from 'src/apolloCache/starredAccounts';
import type {DragEndEvent} from '@dnd-kit/core';
import type {AccountListItemFragment} from '@transactions/graphql';
import type {
  AccountBalanceInfo,
  BalancesById,
} from 'src/AccountsView/accountBalances';
import type {AccountSectionSpec} from 'src/AccountsView/accountFilter';

const STARRED = 'starred';
const TYPE_PREFIX = 'type:';
// Starred rows duplicate accounts that also render in their type section, so
// their sortable IDs are prefixed to keep dnd-kit IDs unique. Starred is
// reorder-only: rows never drag into or out of it (star/unstar via the row
// toggle).
const STARRED_PREFIX = 'starred:';

type Draft = {
  /** All section keys (including empty sections), in display order. */
  typeOrder: string[];
  /** Account IDs per section key, in display order. */
  idsByType: Record<string, string[]>;
  starred: string[];
};

const buildDraft = (
  accounts: AccountListItemFragment[],
  persistedTypeOrder: string[],
  starredIds: string[],
  previous?: Draft,
): Draft => {
  const sections = orderSections(previous?.typeOrder ?? persistedTypeOrder);
  const accountIds = new Set(accounts.map((a) => a._id));

  // Keep the previous draft's per-section order where possible so an
  // in-flight drag isn't clobbered; drop removed accounts, append new ones
  // in list order.
  const idsByType: Record<string, string[]> = {};
  for (const section of sections) {
    const sectionAccountIds = accounts
      .filter((account) => section.matches(account))
      .map((account) => account._id);
    const sectionSet = new Set(sectionAccountIds);
    const kept = (previous?.idsByType[section.key] ?? []).filter((id) =>
      sectionSet.has(id),
    );
    const keptSet = new Set(kept);
    idsByType[section.key] = [
      ...kept,
      ...sectionAccountIds.filter((id) => !keptSet.has(id)),
    ];
  }

  // Same for starred: keep the previous draft's order for accounts that are
  // still starred, append newly starred ones in server order.
  const starredSet = new Set(starredIds.filter((id) => accountIds.has(id)));
  const keptStarred = (previous?.starred ?? []).filter((id) =>
    starredSet.has(id),
  );
  const keptStarredSet = new Set(keptStarred);
  const newlyStarred = starredIds.filter(
    (id) => starredSet.has(id) && !keptStarredSet.has(id),
  );

  return {
    typeOrder: sections.map((section) => section.key),
    idsByType,
    starred: [...keptStarred, ...newlyStarred],
  };
};

type RowProps = {
  account: AccountListItemFragment;
  balance?: AccountBalanceInfo;
  /** Sortable ID override (starred rows use a prefixed ID). */
  sortableId?: string;
  starred: boolean;
  onToggleStar: () => void;
};

const RearrangeAccountRow = ({
  account,
  balance,
  sortableId,
  starred,
  onToggleStar,
}: RowProps) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
    useSortable({id: sortableId ?? account._id});

  return (
    <div
      ref={setNodeRef}
      style={{transform: CSS.Transform.toString(transform), transition}}
      className={isDragging ? 'opacity-50' : ''}
    >
      <AccountRowV2
        account={account}
        balance={balance}
        starred={starred}
        onToggleStar={onToggleStar}
        dragHandle={
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground cursor-grab touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5" />
          </button>
        }
      />
    </div>
  );
};

type SectionProps = {
  section: AccountSectionSpec;
  accountIds: string[];
  accountById: Map<string, AccountListItemFragment>;
  balances: BalancesById;
  collapsed: boolean;
  onToggle: () => void;
  starredSet: Set<string>;
  onToggleStar: (accountId: string) => void;
};

/**
 * A section that is itself sortable (dragged by its header grip).
 * Collapsing works while rearranging — a collapsed section is easier to
 * drag past.
 */
const SortableTypeSection = ({
  section,
  accountIds,
  accountById,
  balances,
  collapsed,
  onToggle,
  starredSet,
  onToggleStar,
}: SectionProps) => {
  const {attributes, listeners, setNodeRef, transform, transition, isDragging} =
    useSortable({id: `${TYPE_PREFIX}${section.key}`});
  const accounts = accountIds.flatMap((id) => {
    const account = accountById.get(id);
    return account ? [account] : [];
  });

  return (
    <div
      ref={setNodeRef}
      style={{transform: CSS.Transform.toString(transform), transition}}
      className={isDragging ? 'opacity-50' : ''}
    >
      <AccountSection
        label={section.label}
        accounts={toSectionAccounts(accounts, balances)}
        collapsed={collapsed}
        onToggle={onToggle}
        dragHandle={
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground cursor-grab touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        }
      >
        <SortableContext items={accountIds} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-2">
            {accounts.map((account) => (
              <RearrangeAccountRow
                key={account._id}
                account={account}
                balance={balances.get(account._id)}
                starred={starredSet.has(account._id)}
                onToggleStar={() => onToggleStar(account._id)}
              />
            ))}
          </div>
        </SortableContext>
      </AccountSection>
    </div>
  );
};

type Props = {
  accounts: AccountListItemFragment[];
  /** Balance per account id, streamed in separately from the metadata. */
  balances: BalancesById;
  /** Persisted type-section key order (may omit types). */
  typeOrder: string[];
  starredIds: string[];
  onToggleStar: (accountId: string) => void;
  /** Shared with the read-only view so collapse state survives mode toggles. */
  isCollapsed: (sectionId: string) => boolean;
  onToggleSection: (sectionId: string) => void;
};

/**
 * Rearrange mode for the sectioned accounts overview: rows get drag grips
 * to reorder within their section, section headers drag to reorder the
 * sections, and the starred section reorders independently. Accounts never
 * move between sections — the section is determined by the account's type
 * (or, for the merged Investments section, its role). Each drop persists
 * immediately (reorderAccounts / reorderAccountTypes /
 * reorderStarredAccounts).
 */
export const RearrangeAccountsView = ({
  accounts,
  balances,
  typeOrder,
  starredIds,
  onToggleStar,
  isCollapsed,
  onToggleSection,
}: Props) => {
  const [draft, setDraftState] = React.useState<Draft>(() =>
    buildDraft(accounts, typeOrder, starredIds),
  );
  const draftRef = React.useRef(draft);
  const setDraft = React.useCallback((next: Draft) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);

  // Reconcile when accounts are created/deleted or starred/unstarred while
  // rearranging
  React.useEffect(() => {
    setDraft(buildDraft(accounts, typeOrder, starredIds, draftRef.current));
  }, [accounts, typeOrder, starredIds, setDraft]);

  const [reorderAccountsMutation] = useMutation(ReorderAccountsDocument);
  const [reorderTypesMutation] = useMutation(ReorderAccountTypesDocument);
  const [reorderStarredMutation] = useMutation(ReorderStarredAccountsDocument);

  const accountById = React.useMemo(
    () => new Map(accounts.map((a) => [a._id, a])),
    [accounts],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 4}}),
  );

  // The global account order is the concatenation of the type sections in
  // display order — reordering within a section rewrites it wholesale.
  const persistAccounts = (next: Draft) => {
    reorderAccountsMutation({
      variables: {
        accounts: next.typeOrder.flatMap((key) => next.idsByType[key] ?? []),
      },
    });
  };

  const persistTypes = (next: Draft) => {
    reorderTypesMutation({
      variables: {typeKeys: next.typeOrder},
      optimisticResponse: {reorderAccountTypes: next.typeOrder},
      update: (cache, result) => {
        const keys = result.data?.reorderAccountTypes;
        if (keys) {
          setAccountTypeOrder(cache, keys);
        }
      },
    });
  };

  const persistStarred = (starred: string[]) => {
    reorderStarredMutation({
      variables: {accountIds: starred},
      optimisticResponse: {reorderStarredAccounts: starred},
      update: (cache, result) => {
        const ids = result.data?.reorderStarredAccounts;
        if (ids) {
          setStarredAccountIds(cache, ids);
        }
      },
    });
  };

  /** The type-section key containing an account row id, if any. */
  const containerOf = (id: string): string | null =>
    draftRef.current.typeOrder.find((key) =>
      (draftRef.current.idsByType[key] ?? []).includes(id),
    ) ?? null;

  const handleDragEnd = ({active, over}: DragEndEvent) => {
    if (!over) {
      return;
    }
    const activeId = String(active.id);
    const overId = String(over.id);
    const current = draftRef.current;

    if (activeId.startsWith(STARRED_PREFIX)) {
      // Reorder within the starred section; drops anywhere else are no-ops
      // (starred rows never leave the section by dragging).
      if (overId.startsWith(STARRED_PREFIX) && overId !== activeId) {
        const fromIndex = current.starred.indexOf(
          activeId.slice(STARRED_PREFIX.length),
        );
        const toIndex = current.starred.indexOf(
          overId.slice(STARRED_PREFIX.length),
        );
        if (fromIndex >= 0 && toIndex >= 0) {
          const next = {
            ...current,
            starred: arrayMove(current.starred, fromIndex, toIndex),
          };
          setDraft(next);
          persistStarred(next.starred);
        }
      }
      return;
    }

    if (activeId.startsWith(TYPE_PREFIX)) {
      // Reorder whole type sections by header drag. The drop target is often
      // an account row rather than another header (rows are droppables in
      // the same DndContext) — resolve rows to their containing section.
      const activeKey = activeId.slice(TYPE_PREFIX.length);
      const overKey = overId.startsWith(TYPE_PREFIX)
        ? overId.slice(TYPE_PREFIX.length)
        : containerOf(overId);
      if (overKey != null && overKey !== activeKey) {
        const fromIndex = current.typeOrder.indexOf(activeKey);
        const toIndex = current.typeOrder.indexOf(overKey);
        if (fromIndex >= 0 && toIndex >= 0) {
          const next = {
            ...current,
            typeOrder: arrayMove(current.typeOrder, fromIndex, toIndex),
          };
          setDraft(next);
          persistTypes(next);
        }
      }
      return;
    }

    // Account rows only reorder within their own type section — a drop over
    // anything outside it (another section's row, a header) is a no-op.
    const container = containerOf(activeId);
    if (container == null || containerOf(overId) !== container) {
      return;
    }
    const ids = current.idsByType[container] ?? [];
    const fromIndex = ids.indexOf(activeId);
    const toIndex = ids.indexOf(overId);
    if (fromIndex >= 0 && toIndex >= 0 && fromIndex !== toIndex) {
      const next = {
        ...current,
        idsByType: {
          ...current.idsByType,
          [container]: arrayMove(ids, fromIndex, toIndex),
        },
      };
      setDraft(next);
      persistAccounts(next);
    }
  };

  const starredSet = new Set(draft.starred);
  const starredAccounts = draft.starred.flatMap((id) => {
    const account = accountById.get(id);
    return account ? [account] : [];
  });
  const sectionByKey = new Map(
    orderSections(draft.typeOrder).map((section) => [section.key, section]),
  );
  // Only sections with accounts render; empty sections keep their relative
  // position in draft.typeOrder for when they gain accounts
  const sections = draft.typeOrder.flatMap((key) => {
    const section = sectionByKey.get(key);
    const ids = draft.idsByType[key] ?? [];
    return section && ids.length > 0 ? [{section, ids}] : [];
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col gap-4">
        {starredAccounts.length > 0 && (
          <SortableContext
            items={draft.starred.map((id) => `${STARRED_PREFIX}${id}`)}
            strategy={verticalListSortingStrategy}
          >
            <AccountSection
              label="Starred"
              badges={
                <Star className="h-3.5 w-3.5 shrink-0 fill-amber-500 text-amber-500" />
              }
              accounts={toSectionAccounts(starredAccounts, balances)}
              collapsed={isCollapsed(STARRED)}
              onToggle={() => onToggleSection(STARRED)}
            >
              {starredAccounts.map((account) => (
                <RearrangeAccountRow
                  key={account._id}
                  account={account}
                  balance={balances.get(account._id)}
                  sortableId={`${STARRED_PREFIX}${account._id}`}
                  starred
                  onToggleStar={() => onToggleStar(account._id)}
                />
              ))}
            </AccountSection>
          </SortableContext>
        )}

        <SortableContext
          items={sections.map(({section}) => `${TYPE_PREFIX}${section.key}`)}
          strategy={verticalListSortingStrategy}
        >
          {sections.map(({section, ids}) => (
            <SortableTypeSection
              key={section.key}
              section={section}
              accountIds={ids}
              accountById={accountById}
              balances={balances}
              collapsed={isCollapsed(section.key)}
              onToggle={() => onToggleSection(section.key)}
              starredSet={starredSet}
              onToggleStar={onToggleStar}
            />
          ))}
        </SortableContext>
      </div>
    </DndContext>
  );
};
